import { useEffect, useRef, useState } from "react";
import {
  createContent,
  deleteContent,
  describeImage,
  listContent,
  updateContent,
  uploadFile,
} from "../lib/resources.ts";
import { pool } from "../lib/bulk.ts";
import type { Content as ContentItem } from "../lib/types.ts";
import AIComposer, { type ComposerSeed } from "./AIComposer.tsx";
import BulkCompose from "./BulkCompose.tsx";
import ContentPreview from "./ContentPreview.tsx";
import TextSlideEditor from "./TextSlideEditor.tsx";
import { forStorage, type TextSlide } from "../lib/text-slide.ts";

// Content — the reusable asset pool (no timing). Create text slides, upload
// images/videos, edit, and delete. (Phase 5 adds the AI composer here.)
export default function Content() {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContentItem | "new" | null>(null);
  // `true` is a blank session; a seed reopens an existing item.
  const [composing, setComposing] = useState<true | ComposerSeed | null>(null);
  const [bulk, setBulk] = useState(false);
  /** Ids currently being described, so each card can show its own spinner. */
  const [describing, setDescribing] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  /** Looking at what has been taken down, rather than what is usable today. */
  const [showArchived, setShowArchived] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function refresh(archived = showArchived) {
    setLoading(true);
    try {
      setItems(await listContent(archived));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load content");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh(showArchived);
  }, [showArchived]);

  /** Set or clear a slide's take-down date. */
  async function setExpiry(item: ContentItem, date: string | null) {
    setError(null);
    try {
      const updated = await updateContent(item.id, { expiresAt: date });
      // An expiry in the past archives the slide, which drops it out of this
      // list — refetch rather than patch a row that may no longer belong here.
      if (updated.archivedAt !== item.archivedAt) {
        await refresh();
        // Otherwise the card just vanishes and it reads as the date being refused.
        if (updated.archivedAt) {
          setNotice(`"${item.name}" is off the screens and moved to Archived.`);
        }
      } else setItems((cur) => cur.map((c) => (c.id === item.id ? updated : c)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't set that date");
    }
  }

  /** Take a slide down now, or bring it back. */
  async function setArchived(item: ContentItem, archived: boolean) {
    setError(null);
    try {
      await updateContent(item.id, { archived });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't change that");
    }
  }

  async function handleUpload(file: File) {
    // The accept="image/*,video/*" filter is only a hint — the file dialog can
    // be switched to "All files". Catch it here so the user gets a useful
    // pointer instead of a failed upload (the API rejects it too).
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/") && file.type !== "image/svg+xml";
    if (!isVideo && !isImage) {
      const isDeck = /\.pptx?$/i.test(file.name);
      setError(
        isDeck
          ? "PowerPoint can’t be uploaded here. Use Sources → PowerPoint — it imports the deck as slides and re-imports whenever you edit it."
          : `“${file.name}” isn’t an image or video, so a screen couldn’t display it.`,
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadFile(file);
      const type = isVideo ? "video" : "image";
      await createContent({
        name: file.name.replace(/\.[^.]+$/, ""),
        type,
        url,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function saveTextSlide(slide: TextSlide, existing: ContentItem | "new") {
    // forStorage drops the fields the chosen layout does not render, so what is
    // saved matches what is on screen.
    const data = forStorage(slide);
    if (existing === "new") {
      await createContent({ name: slide.name, type: "text", data });
    } else {
      await updateContent(existing.id, { name: slide.name, data });
    }
    setEditing(null);
    await refresh();
  }

  /** Describe one image, updating just that card when it lands. */
  async function describe(item: ContentItem) {
    setDescribing((s) => new Set(s).add(item.id));
    setError(null);
    try {
      const described = await describeImage(item.id);
      // Patch in place rather than refetching the whole list — a bulk run would
      // otherwise reload it once per image.
      setItems((cur) =>
        cur.map((c) =>
          c.id === item.id
            ? { ...c, data: { ...(c.data ?? {}), ...described } }
            : c,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't describe that image");
      throw e;
    } finally {
      setDescribing((s) => {
        const n = new Set(s);
        n.delete(item.id);
        return n;
      });
    }
  }

  /** Describe every image that hasn't got one yet — the point of this is an
   *  imported deck, which arrives as a dozen identically-named pages. */
  async function describeAll() {
    const todo = items.filter(
      (i) => i.type === "image" && !(i.data as { summary?: string } | null)?.summary,
    );
    if (!todo.length) return;
    if (
      !confirm(
        `Describe ${todo.length} image${todo.length === 1 ? "" : "s"}? That uses ${todo.length} of today's AI allowance.`,
      )
    )
      return;
    // Errors are swallowed per item: one failure (or the cap running out
    // partway) shouldn't abandon the images that would have worked.
    await pool(todo, 3, async (item) => {
      try {
        await describe(item);
      } catch {
        /* already surfaced in the banner */
      }
    });
  }

  async function remove(item: ContentItem) {
    if (
      !confirm(
        `Delete “${item.name}” for good? This also removes it from any playlists and schedules. ` +
          `To just take it off the screens and keep it, use Take down instead.`,
      )
    )
      return;
    await deleteContent(item.id);
    await refresh();
  }

  const undescribed = items.filter(
    (i) => i.type === "image" && !(i.data as { summary?: string } | null)?.summary,
  ).length;

  return (
    <section className="admin-page">
      <div className="page-head">
        <div>
          <h1>Content</h1>
          <p className="muted">
            {showArchived
              ? "Slides that have been taken down. Nothing here is deleted — give one a new date to bring it back."
              : "The reusable pool of assets. Timing and placement happen later in Schedules."}
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn--ghost"
            title={
              showArchived
                ? "Back to the slides you can use today"
                : "Slides that have come down, kept for next year"
            }
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? "← Back to current" : "🗄 Archived"}
          </button>
          <button className="btn btn--ghost" onClick={() => setComposing(true)}>
            ✨ Compose with AI
          </button>
          <button className="btn btn--ghost" onClick={() => setBulk(true)}>
            ✨ Several at once
          </button>
          {undescribed > 0 && (
            <button
              className="btn btn--ghost"
              disabled={describing.size > 0}
              title="Write alt text and a short label for every image that hasn't got one"
              onClick={() => void describeAll()}
            >
              {describing.size > 0
                ? `Describing… (${describing.size})`
                : `🏷️ Describe ${undescribed} image${undescribed === 1 ? "" : "s"}`}
            </button>
          )}
          <button className="btn btn--ghost" onClick={() => setEditing("new")}>
            ✦ New text slide
          </button>
          <button
            className="btn"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? "Uploading…" : "↑ Upload image / video"}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}
      {notice && !showArchived && (
        <div className="banner banner--ok">
          {notice}{" "}
          <button className="link-btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="admin-empty">
          <strong>No content yet</strong>
          <span>Create a text slide or upload an image to get started.</span>
        </div>
      ) : (
        <div className="card-grid">
          {items.map((item) => (
            <div className="content-card card" key={item.id}>
              <div className="content-card-preview">
                <ContentPreview content={item} />
              </div>
              <div className="content-card-meta">
                <div className="content-card-name" title={item.name}>
                  {item.name}
                </div>
                <span className="badge">{item.type}</span>
              </div>
              {(item.data as { summary?: string } | null)?.summary && (
                <div
                  className="content-card-summary"
                  title={(item.data as { alt?: string }).alt}
                >
                  {(item.data as { summary?: string }).summary}
                </div>
              )}
              {item.createdByName && (
                <div
                  className="content-card-author"
                  title={item.createdByEmail ?? undefined}
                >
                  By {item.createdByName}
                </div>
              )}
              <ExpiryControl
                item={item}
                onSet={(date) => void setExpiry(item, date)}
                onRestore={() => void setArchived(item, false)}
              />
              <div className="content-card-actions">
                {item.type === "text" && (
                  <button
                    className="link-btn"
                    onClick={() => setEditing(item)}
                  >
                    Edit
                  </button>
                )}
                {item.type === "svg" && (
                  <button
                    className="link-btn"
                    title="Reopen this slide and ask for changes"
                    onClick={() =>
                      setComposing({
                        kind: "revise",
                        contentId: item.id,
                        name: item.name,
                        svg: (item.data as { svg?: string } | null)?.svg ?? "",
                      })
                    }
                  >
                    Revise with AI
                  </button>
                )}
                {item.type === "image" && item.url && (
                  <button
                    className="link-btn"
                    title="Send this into the composer and have it redesigned for a TV"
                    onClick={() =>
                      setComposing({
                        kind: "image",
                        name: item.name,
                        url: item.url as string,
                      })
                    }
                  >
                    Redesign with AI
                  </button>
                )}
                {item.type === "image" && (
                  <button
                    className="link-btn"
                    disabled={describing.has(item.id)}
                    title="Write alt text and a short label for this image"
                    onClick={() => void describe(item).catch(() => {})}
                  >
                    {describing.has(item.id)
                      ? "Describing…"
                      : (item.data as { summary?: string } | null)?.summary
                        ? "Re-describe"
                        : "Describe"}
                  </button>
                )}
                {!item.archivedAt && (
                  <button
                    className="link-btn"
                    title="Take it off the screens now, but keep it"
                    onClick={() => void setArchived(item, true)}
                  >
                    Take down
                  </button>
                )}
                <button className="link-btn link-btn--danger" onClick={() => remove(item)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TextSlideEditor
          initial={
            editing === "new"
              ? null
              : {
                  name: editing.name,
                  ...(editing.data as Omit<TextSlide, "name">),
                }
          }
          onCancel={() => setEditing(null)}
          onSave={(slide) => saveTextSlide(slide, editing)}
        />
      )}

      {composing && (
        <AIComposer
          // Remount on a different seed, so reopening a second slide doesn't
          // inherit the first one's transcript.
          key={composing === true ? "new" : `${composing.kind}:${composing.name}`}
          {...(composing === true ? {} : { seed: composing })}
          onClose={() => setComposing(null)}
          onSaved={() => {
            setComposing(null);
            void refresh();
          }}
        />
      )}

      {bulk && (
        <BulkCompose
          onClose={() => setBulk(false)}
          onSaved={() => {
            setBulk(false);
            void refresh();
          }}
        />
      )}
    </section>
  );
}

/**
 * "2026-09-13" means the 13th where the church is, not midnight UTC. Parsed with
 * `new Date("2026-09-13")` it is the latter, which in Illinois is the evening of
 * the 12th — so a bare date is built as a LOCAL date here, the same way the API
 * pushes it to the end of that day when saving.
 */
function asLocalDate(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

const dayLabel = (value: string) =>
  asLocalDate(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

/**
 * The last day a slide is up. A saved "until the 25th" is stored as the moment
 * it comes down — midnight starting the 26th — so read as a day it says the
 * 26th; step back a millisecond onto the day that was picked.
 */
const lastDay = (expiresAt: string) =>
  new Date(asLocalDate(expiresAt).getTime() - 1).toISOString();

const toDateInput = (value: string) => {
  const d = asLocalDate(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * When a slide should stop playing.
 *
 * The date is the one printed on the slide, so the describe pass has usually
 * already read it ("Baptism, Sunday Sept. 13"). Where it has, the control offers
 * that date as one click rather than asking someone to remember it — offered,
 * never applied on its own, because a date read wrong takes a live announcement
 * off the wall early.
 *
 * Nothing here deletes. A slide past its date is archived: off the screens, out
 * of the working list, still there next year when the same event comes round.
 */
function ExpiryControl({
  item,
  onSet,
  onRestore,
}: {
  item: ContentItem;
  onSet: (date: string | null) => void;
  onRestore: () => void;
}) {
  if (item.archivedAt) {
    // Came down on its date (archive stamps the expiry itself) vs. by hand.
    const byDate =
      item.expiresAt != null &&
      Date.parse(item.archivedAt) === Date.parse(item.expiresAt);
    return (
      <div className="content-card-expiry">
        <span className="muted">
          {byDate
            ? `Ran through ${dayLabel(lastDay(item.expiresAt as string))}`
            : `Taken down ${dayLabel(item.archivedAt)}`}
        </span>
        <button className="link-btn" onClick={onRestore}>
          Restore
        </button>
      </div>
    );
  }

  const value = item.expiresAt ? toDateInput(lastDay(item.expiresAt)) : "";
  return (
    <div className="content-card-expiry">
      <label title="After this day it comes off the screens and moves to Archived">
        <span className="muted">Until</span>{" "}
        {/* Uncontrolled, and only a whole date (or a real clear) is saved: while
            someone types "10/25/2026" the input reports "" after each partial
            segment, and saving that cleared the date and wiped what they'd typed. */}
        <input
          type="date"
          key={value}
          defaultValue={value}
          onChange={(e) => {
            if (e.target.value) onSet(e.target.value);
            else if (!e.target.validity.badInput) onSet(null);
          }}
        />
      </label>
      {!value && item.suggestedExpiry && (
        <button
          className="link-btn"
          title="The date this slide has printed on it"
          onClick={() => onSet(item.suggestedExpiry as string)}
        >
          Use {dayLabel(item.suggestedExpiry)}
        </button>
      )}
    </div>
  );
}
